import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { locationCounts, locationCountsScript } from "@/content/locationCounts";
import { locationCountsCms } from "@/content/locationSource";
import { LOCATION_MENU_CODES, type FeatureLink } from "./navigation";

/**
 * The IP counts the Locations menu prints, as the page was rendered with them
 * (`src/content/locationCounts.ts`).
 */
type Counts = Readonly<Record<string, string>>;

const HeaderCounts = createContext<Counts | null>(null);

/** The counts the menu prints, and nothing else: what the page hands the browser. */
function menuCounts(counts: Counts): Counts {
  return Object.fromEntries(
    LOCATION_MENU_CODES.flatMap((code) =>
      counts[code] === undefined ? [] : [[code, counts[code]]]
    )
  );
}

/**
 * The counts this page was rendered with: on the server those read for the
 * request, in the browser those the page wrote. A page the browser drew
 * itself, having moved to it from a dashboard page, carries none, and asks for
 * them once; the menu they sit in is closed until hovered.
 */
function useHeaderCountsRead(): Counts | null {
  const [counts, setCounts] = useState<Counts | null>(
    () => locationCounts() ?? null
  );
  useEffect(() => {
    if (counts) return undefined;
    let current = true;
    void locationCountsCms().then((read) => {
      if (!current) return;
      window.__LOCATION_COUNTS__ = menuCounts(read);
      setCounts(read);
    });
    return () => {
      current = false;
    };
  }, [counts]);
  return counts;
}

/**
 * Hands the header's rows their counts, and writes the ones they print into
 * the page, for the browser to read before it hydrates.
 */
export function HeaderCountsProvider({ children }: { children: ReactNode }) {
  const counts = useHeaderCountsRead();
  return (
    <HeaderCounts.Provider value={counts}>
      {children}
      {counts ? (
        <script
          dangerouslySetInnerHTML={{
            __html: locationCountsScript(menuCounts(counts)),
          }}
        />
      ) : null}
    </HeaderCounts.Provider>
  );
}

/** A row's summary line: a country's IP count before its unit, else the line as written. */
export function useSummary(link: FeatureLink): string {
  const counts = useContext(HeaderCounts);
  if (!link.countOf) return link.summary;
  const count = counts?.[link.countOf];
  return count ? `${count} ${link.summary}` : "";
}
